import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import {
  Button,
  GlobalStyles,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { type ReactNode } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { useLocation, useNavigate } from 'react-router-dom'

import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

function PageErrorFallback({ resetErrorBoundary }: FallbackProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { partner } = usePartnerCentralized()

  return (
    <>
      <GlobalStyles
        styles={{
          '#content-wrapper-page-layout > div': {
            height: '100%',
          },
        }}
      />
      <Stack
        sx={{
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.common.white,
            border: 1,
            borderColor: theme.palette.divider,
            borderRadius: rem(16),
            maxWidth: rem(560),
            width: '100%',
            py: rem(48),
            px: rem(24),
            textAlign: 'center',
            gap: rem(12),
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              width: rem(48),
              height: rem(48),
              borderRadius: '50%',
              backgroundColor: theme.palette.mainBackground,
            }}
          >
            <ErrorOutlineRoundedIcon
              sx={{ fontSize: rem(28), color: theme.palette.error.light }}
            />
          </Stack>

          <Typography variant="h5" color={theme.palette.mainFont}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color={theme.palette.text.secondary}>
            This page couldn&apos;t load correctly. Try refreshing the page or
            going back.
          </Typography>

          <Stack sx={{ mt: rem(8) }} spacing={2}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              flexWrap="wrap"
              spacing={2}
              useFlexGap
            >
              <Button variant="primaryModern" onClick={resetErrorBoundary}>
                Try again
              </Button>
              <Button
                variant="tertiary1SecondaryModern"
                onClick={() => {
                  window.location.reload()
                }}
              >
                Reload page
              </Button>
              <Button
                variant="tertiary1Modern"
                onClick={() => {
                  navigate(EClientPath.home)
                }}
              >
                Go home
              </Button>
            </Stack>

            {partner.supportEmail ? (
              <Link
                href={`mailto:${partner.supportEmail}`}
                underline="always"
                color="primary"
              >
                Contact support
              </Link>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}

type TProps = {
  children: ReactNode
}

/** Wraps a page (or route segment) to show a full-page error UI on render errors. */
export default function PageErrorBoundary({ children }: TProps) {
  const location = useLocation()

  return (
    <ErrorBoundary
      resetKeys={[location.key]}
      FallbackComponent={PageErrorFallback}
    >
      {children}
    </ErrorBoundary>
  )
}
